import React, { FC, ReactNode } from 'react';
import MaterialTable, { MTableToolbar, Components } from 'material-table';

import AddIcon from '@material-ui/icons/Add';
import DeleteSweepIcon from '@material-ui/icons/DeleteSweep';
import { Paper } from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import DateCell from '../common/DateCell';
import VideoPlayer from '../common/VideoPlayer';
import { useLanguageContext } from '../languages';
import { DRMTYPE, ExclusionPeriod } from '../../utils/types';
import { getLocalTimezone } from '../../utils/dateTransform';

const ExclusionPeriods: FC<ExclusionPeriodsProps> = ({ drmType, items, laSource, onShowOptions, onRemoveAll, onRemove, videoUrl }) => {
	const { tableLocalization } = useLanguageContext();

	const tableColumns = [
		{
			title: 'Title',
			field: 'title'
		},
		{
			title: 'Player',
			field: 'shortText'
		},
		{
			title: `Start Time (${getLocalTimezone()})`,
			field: 'startTime',
			cellStyle: { minWidth: 175 },
			render: (row: ExclusionPeriod): JSX.Element => <DateCell value={row.startTime ?? ''} showTimezone={false} />
		},
		{
			title: `End Time (${getLocalTimezone()})`,
			field: 'endTime',
			cellStyle: { minWidth: 175 },
			render: (row: ExclusionPeriod): JSX.Element => <DateCell value={row.endTime ?? ''} showTimezone={false} />
		},
		{
			title: 'Period ID',
			field: 'id'
		},
		{
			title: 'Play ID',
			field: 'playId'
		}
	];

	const tableComponents: Components = {
		Container: (props): JSX.Element => <Paper {...props} data-testid='exclusion-periods-table' />,
		Toolbar: (props): JSX.Element => (
			<>
				<MTableToolbar {...props} />
				{drmType && laSource && (
					<Alert severity='info' style={{ marginBottom: '0.5rem' }}>
						A VPN connection is required to watch a LIVE stream.
						<br />
						DRM protected content may cease to be viewable after the LIVE stream ends.
					</Alert>
				)}
				<Alert severity='info'>The following clips will be excluded from all live highlights on this airing.</Alert>
			</>
		)
	};

	const showPreview = (rowData: ExclusionPeriod): ReactNode => {
		if (drmType && laSource)
			return (
				<VideoPlayer
					drmType={drmType.toLowerCase() as DRMTYPE}
					laSource={laSource}
					source={videoUrl}
					timeShift={true}
					videoEnd={rowData.endTime || ''}
					videoStart={rowData.startTime || ''}
				/>
			);
		return <VideoPlayer source={videoUrl} timeShift={false} videoEnd={rowData.endTime || ''} videoStart={rowData.startTime || ''} />;
	};

	// prettier-ignore
	const actions = [
        ...(onRemoveAll
            ? [{
                icon: DeleteSweepIcon,
                tooltip: 'Remove All',
                isFreeAction: true,
                onClick: onRemoveAll,
            }]
            : []),
        ...(onShowOptions
            ? [{
                icon: AddIcon,
                tooltip: 'Select',
                isFreeAction: true,
                onClick: onShowOptions,
            }]
            : []),
    ];

	return (
		<MaterialTable
			title={'Excluded Clips'}
			localization={tableLocalization}
			columns={tableColumns}
			options={{ pageSize: 5, search: false, sorting: false }}
			data={items}
			actions={actions}
			editable={{
				onRowDelete: onRemove
			}}
			components={tableComponents}
			detailPanel={(rowData): ReactNode => showPreview(rowData)}
		/>
	);
};

type ExclusionPeriodsProps = {
	drmType?: DRMTYPE;
	items: ExclusionPeriod[];
	laSource?: string;
	onRemoveAll?: () => void;
	onRemove?: (data: ExclusionPeriod) => Promise<unknown>;
	onShowOptions?: () => void;
	videoUrl: string;
};

export default ExclusionPeriods;
